import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Loader2, Send, Sparkles, Trash2, X } from 'lucide-react'
import { supabase } from '../../../lib/supabaseClient'
import { useShowAiProvider } from '../../../hooks/useShowAiProvider'
import { cn } from '../../../lib/utils'

// AI assistant for immigration planning. Grounded in:
//   - the user's profile + finance snapshot
//   - all immigration_countries reference rows
//   - the user's per-country tracking (state, docs, milestones, costs)
//
// Multi-turn, client-side history. If `countrySlug` is passed,
// the edge function focuses its system prompt on that country
// (full payload for one, stubs for the rest).

const SUGGESTIONS_AR_GLOBAL = [
  'مقارنة كندا وألمانيا بناء على ملفى',
  'ايه أنسب دولة من القائمة؟',
  'حسبلى CRS Express Entry تقريبى',
  'ايه الـ docs اللى لازم أبدأ بيها؟',
]
const SUGGESTIONS_EN_GLOBAL = [
  'Compare Canada and Germany for my profile',
  'Which country fits me best?',
  'Estimate my Express Entry CRS score',
  'What docs should I start with?',
]
const SUGGESTIONS_AR_FOCUSED = [
  'ايه المسار الأنسب لى هنا؟',
  'حسبلى التكلفة الكلية المتوقعة',
  'ايه الخطوة الجاية المفروض أعملها؟',
  'هل أقدر أتحملها ماليا؟',
]
const SUGGESTIONS_EN_FOCUSED = [
  'What pathway fits me best here?',
  'Estimate total expected cost',
  'What\'s the next step I should take?',
  'Can I afford this financially?',
]

export default function ImmigrationAssistantSheet({ open, countrySlug = null, onClose }) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const [question, setQuestion] = useState('')
  const [messages, setMessages] = useState([])
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)
  const scrollRef = useRef(null)
  const { enabled: showProvider } = useShowAiProvider()

  useEffect(() => {
    setMessages([])
    setQuestion('')
    setError(null)
  }, [countrySlug, open])

  useEffect(() => {
    if (!open) return
    const r = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(r)
  }, [open])

  useEffect(() => {
    const prev = document.body.style.overflow
    if (open) document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, pending])

  const handleAsk = useCallback(async (q) => {
    const text = (q ?? question).trim()
    if (!text || pending) return
    setQuestion('')
    setError(null)
    const nextMessages = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setPending(true)
    try {
      const { data, error: err } = await supabase.functions.invoke('immigration-assistant', {
        body: {
          messages: nextMessages,
          country_slug: countrySlug,
          language: isAr ? 'ar' : 'en',
        },
      })
      if (err) throw err
      if (data?.error) setError(data.error)
      else if (data?.answer) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.answer, provider: data.provider }])
      } else {
        setError('Empty response')
      }
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setPending(false)
    }
  }, [question, pending, messages, countrySlug, isAr])

  const handleSubmit = (e) => {
    e.preventDefault()
    handleAsk()
  }
  const handleClear = () => { setMessages([]); setError(null) }

  const suggestions = countrySlug
    ? (isAr ? SUGGESTIONS_AR_FOCUSED : SUGGESTIONS_EN_FOCUSED)
    : (isAr ? SUGGESTIONS_AR_GLOBAL : SUGGESTIONS_EN_GLOBAL)

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/70 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-background mt-auto rounded-t-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-input flex-shrink-0">
          <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <h2 className="flex-1 text-base font-semibold text-foreground" dir="auto">
            {t('immigration.assistant.title')}
          </h2>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('assistant.clearChat')}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-accent"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
          {messages.length === 0 && !pending ? (
            <div className="text-center py-6 space-y-3">
              <p className="text-sm text-muted-foreground" dir="auto">
                {t('immigration.assistant.intro')}
              </p>
              <div className="flex flex-wrap gap-1.5 justify-center px-3">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleAsk(s)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full bg-muted hover:bg-accent text-foreground"
                    dir="auto"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m, idx) => (
                <Bubble key={idx} message={m} isAr={isAr} showProvider={showProvider} />
              ))}
              {pending && (
                <div className="inline-flex items-center gap-2 text-xs text-muted-foreground py-1.5 px-3 rounded-2xl bg-muted/60" dir="auto">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>{t('assistant.thinking')}</span>
                </div>
              )}
            </>
          )}
          {error && (
            <p className="text-xs text-red-600 px-2 py-1.5 rounded bg-red-500/10" dir="auto">
              {t('assistant.error', { error })}
            </p>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={handleSubmit} className="flex items-end gap-2 p-3 border-t border-input flex-shrink-0">
          <input
            ref={inputRef}
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('immigration.assistant.placeholder')}
            disabled={pending}
            className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={pending || !question.trim()}
            aria-label={t('assistant.send')}
            className={cn(
              'h-10 w-10 rounded-md inline-flex items-center justify-center transition-colors flex-shrink-0',
              question.trim() && !pending
                ? 'bg-app-primary-500 text-white hover:bg-app-primary-500/90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rtl:-scale-x-100" />}
          </button>
        </form>
      </div>
    </div>,
    document.body,
  )
}

function Bubble({ message, isAr, showProvider }) {
  const isUser = message.role === 'user'
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-app-primary-500 text-white rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm',
        )}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        {message.provider && !isUser && showProvider && (
          <p className="text-[9px] text-muted-foreground/70 font-mono mt-1" dir="ltr">
            via {message.provider}
          </p>
        )}
      </div>
    </div>
  )
}
